import React from "react";
import { useParams, Link } from "react-router-dom";
import { UserPlus, Banknote, Gift, Target, GraduationCap, Scale, LifeBuoy, UsersRound, Sparkles, ArrowRight } from "lucide-react";

const MODULES = {
  recruiting: {
    icon: UserPlus, phase: "Phase 2", name: "招聘 ATS", en: "Recruiting & ATS",
    desc: "从职位发布到 Offer 的全流程招聘管理，AI 驱动简历筛选（设计方案模块 03）",
    features: [
      "职位发布与候选人管道看板（新简历 → 筛选 → 面试 → Offer → 入职）",
      "AI 简历解析与岗位匹配评分（自动比对 21 个岗位 JD）",
      "面试安排、评价表与多轮反馈汇总",
      "Offer 审批流与入职流程自动衔接（联动现有入职编排）",
      "建筑行业渠道管理：Indeed / ZipRecruiter / 华人渠道",
      "人才库沉淀：未录用候选人归档，二次激活",
    ],
  },
  compensation: {
    icon: Banknote, phase: "Phase 2", name: "薪酬激励", en: "Compensation & Incentives",
    desc: "四类激励自动计算，直采现有项目与财务收款数据（设计方案模块 07/08）",
    features: [
      "销售提成：按签约与收款进度自动计算（直采 CRM + 财务数据）",
      "PM 项目奖金：按项目毛利与工期达成自动核算（直采项目财务）",
      "设计师计件与管理层年度分红规则引擎",
      "加州加班工资合规核算（联动现有考勤 OT 1.5× / 2× 数据）",
      "薪酬带宽管理与调薪审批流",
      "多州多法人 Payroll 对接（Gusto / ADP）",
    ],
  },
  benefits: {
    icon: Gift, phase: "Phase 3", name: "福利管理", en: "Benefits",
    desc: "保险、退休金与福利资格全生命周期管理（设计方案模块 09）",
    features: [
      "医疗 / 牙科 / 视力保险开放注册（Open Enrollment）",
      "401(k) 退休金计划管理与供款追踪",
      "Workers' Comp 保单与工伤模块联动",
      "福利资格日历：入职等待期自动提醒（联动入职编排）",
      "COBRA 离职福利衔接（联动离职流程）",
      "福利成本分摊到法人与成本中心",
    ],
  },
  performance: {
    icon: Target, phase: "Phase 2", name: "绩效考核", en: "Performance",
    desc: "项目制 KPI 直采，数据说话的绩效体系（设计方案模块 10）",
    features: [
      "项目 KPI 直采：工期达成率、项目毛利、客户满意度自动取数",
      "季度 OKR 目标设定与进度追踪",
      "360° 多维评价（上级 / 同事 / 跨部门协作方）",
      "九宫格绩效校准会议支持",
      "绩效结果自动联动激励计算与晋升提名",
      "试用期 30/60/90 天转正评估（联动入职编排）",
    ],
  },
  training: {
    icon: GraduationCap, phase: "Phase 3", name: "培训发展", en: "Training & Development",
    desc: "安全合规培训与岗位技能成长路径（设计方案模块 11）",
    features: [
      "OSHA 10/30 安全培训计划与证书到期提醒（联动证书预警）",
      "岗位技能矩阵：每个岗位的必备技能与认证追踪",
      "新人 30/60/90 天培训路径（联动入职任务清单）",
      "内部课程库与学习记录",
      "培训完成率看板与提醒",
      "晋升发展计划与人才盘点联动",
    ],
  },
  relations: {
    icon: Scale, phase: "Phase 3", name: "员工关系", en: "Employee Relations",
    desc: "反馈、申诉与劳资合规管理（设计方案模块 12）",
    features: [
      "员工反馈与匿名申诉通道",
      "纪律处分记录与绩效改进计划（PIP）",
      "加州劳资合规文档：工资单明细、休息 / 餐歇合规记录",
      "劳动争议案件档案与跟进",
      "eNPS 敬业度调研与趋势分析",
      "政策签收：员工手册 / 安全制度电子签确认",
    ],
  },
  safety: {
    icon: LifeBuoy, phase: "Phase 3", name: "安全与工伤", en: "Safety & Workers' Comp",
    desc: "工地安全与工伤案件全流程（设计方案模块 13）",
    features: [
      "工地安全检查表与巡检记录（关联现有项目地址）",
      "事故 / 未遂事件（Near Miss）上报与调查",
      "工伤 Workers' Comp 案件追踪：上报 → 就医 → 理赔 → 复工",
      "OSHA 300 / 300A 日志自动生成",
      "安全培训到期联动（OSHA 证书预警）",
      "分包商安全资质检查（联动分包商管理）",
    ],
  },
  talent: {
    icon: UsersRound, phase: "Phase 3", name: "人才盘点", en: "Talent Review",
    desc: "九宫格人才地图与继任计划（设计方案模块 14）",
    features: [
      "九宫格人才地图（绩效 × 潜力）",
      "关键岗位继任计划：PM / 各部门经理后备梯队",
      "高潜人才识别与个人发展计划（IDP）",
      "离职风险 AI 预警：负载、薪酬对标、考勤异常信号",
      "内部人才流动与转岗推荐",
      "与绩效考核、培训发展模块数据联动",
    ],
  },
};

export default function HRMSComingSoon() {
  const { module } = useParams();
  const m = MODULES[module];
  if (!m) return <div className="p-8 text-center text-slate-400">Module not found 模块不存在</div>;
  const Icon = m.icon;
  return (
    <div className="max-w-3xl space-y-5" data-testid={`hrms-coming-${module}`}>
      <div className="flex items-start gap-4">
        <div className="rounded-2xl bg-slate-800 p-3.5"><Icon className="h-7 w-7 text-amber-400" /></div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-extrabold text-slate-800">{m.name}</h1>
            <span className="text-[11px] px-2.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-bold">{m.phase} · 规划中</span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">{m.en}</p>
          <p className="text-sm text-slate-500 mt-2">{m.desc}</p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-1.5 mb-3"><Sparkles className="h-4 w-4 text-amber-500" />模块蓝图（依据《总体设计方案 V1.0》）</p>
        <div className="space-y-2.5">
          {m.features.map((f, i) => (
            <div key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
              <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-400 flex-shrink-0" />{f}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-4 flex flex-wrap items-center gap-3">
        <p className="text-xs text-slate-500 flex-1">该模块将在后续迭代中开发，与现有员工档案、项目、财务数据自动打通。想优先开发此模块？在对话中告诉我即可。</p>
        <Link to="/hrms" data-testid="hrms-coming-back" className="inline-flex items-center gap-1 text-xs rounded-full bg-slate-800 hover:bg-slate-700 text-white font-semibold px-4 py-2">返回数据仪表盘<ArrowRight className="h-3 w-3" /></Link>
      </div>
    </div>
  );
}
